import { useTranslator } from "@eo-locale/react";

import {
  Box,
  Button,
  ButtonGroup,
  ArrowLeftIcon,
  ArrowRightIcon
} from "@/storybook-components";

export interface PaginationProps {
  total: number;
  pageSize: number;
  currentPage: number;
  paginate: (currentPage: number | string) => void;
}

const Pagination = ({
  paginate,
  total,
  pageSize,
  currentPage
}: PaginationProps): JSX.Element => {
  const { translate } = useTranslator();
  /*
    pageSize: Number of data items per page
    total: Total number of data items
  */
  const pageNumbers = [];
  let firstSetOfPages = [];
  let middleSetOfPages = [];
  let lastSetOfPages = [];
  const totalPages = Math.ceil(total / pageSize);

  for (let i = 1; i <= Math.ceil(total / pageSize); i++) {
    pageNumbers.push(i);
  }

  const pagination = (currentValue: "previous" | "next" | number) => {
    if (currentValue === "previous" && currentPage !== 1) {
      paginate(currentPage - 1);
    }

    if (currentValue === "next" && currentPage !== totalPages) {
      paginate(currentPage + 1);
    }

    if (currentValue !== "previous" && currentValue !== "next") {
      paginate(currentValue);
    }
  };

  const renderPagination = () => {
    if (currentPage <= 5) firstSetOfPages = pageNumbers;

    if (totalPages > 5) {
      if (currentPage < 5) {
        firstSetOfPages = pageNumbers.slice(0, 5);
        lastSetOfPages = [totalPages];
      } else if (currentPage > totalPages - 5) {
        firstSetOfPages = pageNumbers.slice(0, 1);
        lastSetOfPages = pageNumbers.slice(totalPages - 5, totalPages);
      } else {
        firstSetOfPages = pageNumbers.slice(0, 1);
        middleSetOfPages = pageNumbers.slice(currentPage - 2, currentPage + 1);
        lastSetOfPages = [totalPages];
      }
    }

    return (
      <>
        {firstSetOfPages.map((number) => renderEachItem(number))}
        {middleSetOfPages.length > 0 && (
          <>
            ...
            {middleSetOfPages.map((number) => renderEachItem(number))}
          </>
        )}
        {lastSetOfPages.length > 0 && (
          <>
            ...
            {lastSetOfPages.map((number) => renderEachItem(number))}
          </>
        )}
      </>
    );
  };

  const renderEachItem = (number: number) => {
    return (
      <Button
        key={number}
        variant="tertiary"
        onClick={() => pagination(number)}
        isActive={number === currentPage}
        className={`ehq-paginationButton ${
          number === currentPage ? "ehq-paginationButtonCurrent" : ""
        }`}
      >
        {number}
      </Button>
    );
  };

  return (
    <ButtonGroup size="sm">
      <Button
        variant="tertiary"
        leftIcon={<ArrowLeftIcon />}
        isDisabled={currentPage === 1}
        onClick={() => pagination("previous")}
        className="ehq-paginationButton ehq-paginationPreviousButton"
      >
        <Box as="span" display={["none", "initial"]}>
          {translate("common.previous")}
        </Box>
      </Button>

      {renderPagination()}

      <Button
        variant="tertiary"
        rightIcon={<ArrowRightIcon />}
        onClick={() => pagination("next")}
        isDisabled={currentPage === totalPages}
        className="ehq-paginationButton ehq-paginationNextButton"
      >
        <Box as="span" display={["none", "initial"]}>
          {translate("common.next")}
        </Box>
      </Button>
    </ButtonGroup>
  );
};

export default Pagination;
